import type { Auth as FirebaseAuth } from 'firebase/auth';
import { env } from './env';
import { rememberPortalAccount } from './portal-account';

export interface SessionUser {
  uid: string;
  email: string;
}

export type AuthStatus = 'loading' | 'signed-in' | 'signed-out';

export interface AuthState {
  status: AuthStatus;
  user: SessionUser | null;
  /** Set when Tab Service rejected the session and we signed the user out. */
  expired: boolean;
}

const MOCK_SESSION_KEY = 'tabfile.mock.session';
const MOCK_PASSWORD = 'demo';

/**
 * Auth lives outside React so the router can wait for it in beforeLoad and the
 * API client can read a token without a hook.
 */
class AuthStore {
  private state: AuthState = { status: 'loading', user: null, expired: false };
  private listeners = new Set<() => void>();
  private readyPromise: Promise<AuthState> | null = null;
  private settleReady: ((state: AuthState) => void) | null = null;
  private firebaseAuth: FirebaseAuth | null = null;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): AuthState => this.state;

  private set(next: Partial<AuthState>): void {
    this.state = { ...this.state, ...next };
    if (this.state.status !== 'loading' && this.settleReady) {
      const settle = this.settleReady;
      this.settleReady = null;
      settle(this.state);
    }
    for (const listener of this.listeners) listener();
  }

  /** Resolves once we know whether somebody is signed in. */
  ready(): Promise<AuthState> {
    if (this.state.status !== 'loading') return Promise.resolve(this.state);
    if (!this.readyPromise) {
      this.readyPromise = new Promise<AuthState>((resolve) => {
        this.settleReady = resolve;
      });
    }
    return this.readyPromise;
  }

  /** Called once at start up. */
  async start(): Promise<void> {
    if (env.mock) {
      this.set({ status: this.readMockSession() ? 'signed-in' : 'signed-out', user: this.readMockSession() });
      return;
    }
    try {
      const auth = await this.getFirebaseAuth();
      const { onAuthStateChanged } = await import('firebase/auth');
      onAuthStateChanged(auth, (user) => {
        this.set({
          status: user ? 'signed-in' : 'signed-out',
          user: user && user.email ? { uid: user.uid, email: user.email } : null,
        });
      });
    } catch (error) {
      console.error('Firebase could not start', error);
      this.set({ status: 'signed-out', user: null });
    }
  }

  async signIn(email: string, password: string): Promise<void> {
    const trimmed = email.trim();
    if (env.mock) {
      if (password !== MOCK_PASSWORD) {
        throw new Error('Incorrect password. Demo mode uses the password: demo');
      }
      const user: SessionUser = { uid: `mock_${btoa(trimmed).replace(/=+$/, '')}`, email: trimmed };
      localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify(user));
      this.set({ status: 'signed-in', user, expired: false });
      return;
    }
    const auth = await this.getFirebaseAuth();
    const { signInWithEmailAndPassword } = await import('firebase/auth');
    try {
      await signInWithEmailAndPassword(auth, trimmed, password);
      this.set({ expired: false });
    } catch (error) {
      throw new Error(friendlyFirebaseMessage(error));
    }
  }

  /**
   * Sign in with a ticket the Tab Service portal handed over (see routes/sso.tsx).
   *
   * The API trades the ticket for a Firebase custom token for this person's own account,
   * and the browser signs in with that. Returns the path the portal asked to land on, if any.
   * Every failure reads the same to the person: the ticket is used up either way, and the
   * way back is through the portal.
   */
  async signInFromPortal(ticket: string): Promise<string | null> {
    if (env.mock) {
      const user: SessionUser = { uid: 'mock_portal', email: 'portal@example.com' };
      localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify(user));
      this.set({ status: 'signed-in', user, expired: false });
      return null;
    }
    let response: Response;
    try {
      response = await fetch(`${env.apiBaseUrl}/v1/sso/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket }),
      });
    } catch {
      throw new Error('Tab Service could not be reached. Check your connection and try again.');
    }
    if (!response.ok) {
      let detail = 'This sign-in link is not valid or has expired. Sign in again.';
      try {
        const problem = (await response.json()) as { detail?: string; status?: number };
        if (problem.detail && (problem.status ?? response.status) < 500) detail = problem.detail;
      } catch {
        // The sentence above is the answer.
      }
      throw new Error(detail);
    }
    const { token, returnTo } = (await response.json()) as { token: string; returnTo?: string | null };
    const auth = await this.getFirebaseAuth();
    const { signInWithCustomToken } = await import('firebase/auth');
    try {
      await signInWithCustomToken(auth, token);
      this.set({ expired: false });
    } catch (error) {
      throw new Error(friendlyFirebaseMessage(error));
    }
    return returnTo ?? null;
  }

  /**
   * Keep the session alive after somebody answered the idle warning with Continue.
   *
   * Firebase refreshes its own token in the background; this asks for a fresh one anyway so
   * the answer to Continue is a real round trip rather than a closed dialog, the way the
   * portal's Continue calls its refresh endpoint.
   */
  async refreshSession(): Promise<void> {
    if (env.mock) return;
    const auth = await this.getFirebaseAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('There is no session to extend. Sign in again.');
    await user.getIdToken(true);
  }

  async signOut(options: { expired?: boolean } = {}): Promise<void> {
    // Whether the last person also had a portal account says nothing about the next one.
    rememberPortalAccount(false);
    if (env.mock) {
      localStorage.removeItem(MOCK_SESSION_KEY);
      this.set({ status: 'signed-out', user: null, expired: options.expired ?? false });
      return;
    }
    const auth = await this.getFirebaseAuth();
    const { signOut } = await import('firebase/auth');
    await signOut(auth);
    this.set({ status: 'signed-out', user: null, expired: options.expired ?? false });
  }

  /**
   * Tab Service refused a session that Firebase accepted (not invited, disabled, unverified).
   * The reason is kept in memory only, never in the URL, and read once by the sign-in page.
   */
  private deniedReason: string | null = null;

  async denySession(reason: string): Promise<void> {
    this.deniedReason = reason;
    await this.signOut();
  }

  consumeDeniedReason(): string | null {
    const reason = this.deniedReason;
    this.deniedReason = null;
    return reason;
  }

  /**
   * The reset link is sent by the API from Tab Service's own mailbox, not by Firebase from a
   * Google address. The API answers the same whether or not the address is known.
   */
  async sendPasswordReset(email: string): Promise<void> {
    if (env.mock) return;
    let response: Response;
    try {
      response = await fetch(`${env.apiBaseUrl}/v1/auth/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
    } catch {
      throw new Error('Tab Service could not be reached. Check your connection and try again.');
    }
    if (response.status === 429) {
      throw new Error('Too many requests for this address. Wait a minute and try again.');
    }
    if (!response.ok) {
      throw new Error('The link could not be sent. Check the address and try again.');
    }
  }

  /**
   * Whose account this reset link is for, or a refusal to show instead of the form.
   *
   * The link in the email is Firebase's, but it is opened on Tab Service's own page rather
   * than Firebase's unbranded one (TS-8285), so the code is checked here.
   */
  async verifyPasswordReset(oobCode: string): Promise<string> {
    if (env.mock) return 'you@company.com';
    const auth = await this.getFirebaseAuth();
    const { verifyPasswordResetCode } = await import('firebase/auth');
    try {
      return await verifyPasswordResetCode(auth, oobCode);
    } catch (error) {
      throw new Error(friendlyFirebaseMessage(error));
    }
  }

  /** Set the password this link was sent for. The link cannot be used again afterwards. */
  async completePasswordReset(oobCode: string, password: string): Promise<void> {
    if (env.mock) return;
    const auth = await this.getFirebaseAuth();
    const { confirmPasswordReset } = await import('firebase/auth');
    try {
      await confirmPasswordReset(auth, oobCode, password);
    } catch (error) {
      throw new Error(friendlyFirebaseMessage(error));
    }
  }

  /**
   * The bearer token for a request. Waits for Firebase to restore the session first: after
   * a reload, the uploads tray asks for upload status before Firebase has said who is signed
   * in, and a request sent in that gap would carry no token, be answered 401, and sign the
   * person out for nothing.
   */
  async getToken(): Promise<string | null> {
    if (env.mock) {
      const user = this.state.user ?? this.readMockSession();
      return user ? `mock-id-token.${user.email}` : null;
    }
    await this.ready();
    const auth = this.firebaseAuth ?? (await this.getFirebaseAuth());
    const user = auth.currentUser;
    return user ? user.getIdToken() : null;
  }

  /**
   * Tab Service answered 401 to a request that carried a token: the session is over. A 401
   * to a request that carried none says nothing about the session and is left alone.
   */
  handleUnauthorized(hadToken: boolean): void {
    if (!hadToken) return;
    if (this.state.status === 'signed-out') return;
    void this.signOut({ expired: true });
  }

  clearExpired(): void {
    if (this.state.expired) this.set({ expired: false });
  }

  private readMockSession(): SessionUser | null {
    try {
      const raw = localStorage.getItem(MOCK_SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as SessionUser;
      return parsed.email ? parsed : null;
    } catch {
      return null;
    }
  }

  private async getFirebaseAuth(): Promise<FirebaseAuth> {
    if (this.firebaseAuth) return this.firebaseAuth;
    if (!env.firebaseConfigured) {
      throw new Error('Sign in is not configured. Set the VITE_FIREBASE values or run with VITE_MOCK=1.');
    }
    const { initializeApp, getApps, getApp } = await import('firebase/app');
    const { getAuth } = await import('firebase/auth');
    const app = getApps().length > 0 ? getApp() : initializeApp(env.firebase);
    this.firebaseAuth = getAuth(app);
    return this.firebaseAuth;
  }
}

function friendlyFirebaseMessage(error: unknown): string {
  const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code: unknown }).code) : '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/user-disabled':
      return 'That account is disabled. Ask your owner or Tab Service to enable it.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'That email and password do not match.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a minute and try again.';
    case 'auth/network-request-failed':
      return 'Could not reach the sign in service. Check your connection.';
    // The password-reset link: expired, already used, or not a link this project issued.
    case 'auth/expired-action-code':
      return 'That link has expired. Ask for a new one from Forgot password on the sign-in page.';
    case 'auth/invalid-action-code':
      return 'That link has already been used or is not valid. Ask for a new one from Forgot password on the sign-in page.';
    case 'auth/weak-password':
      return 'That password is too short. Use at least eight characters.';
    default:
      return 'Sign in failed. Try again, and contact Tab Service if it continues.';
  }
}

export const authStore = new AuthStore();
